// Chatbot.js
import React, { useState, useEffect, useRef } from "react";
import { ReactComponent as WhatsAppIcon } from "../icons/iconWhatsapp.svg";

// --- CONSTANTS ---
const WHATSAPP_NUMBER = "919428958799";
const DISPLAY_PHONE_NUMBER = "+91 94289 58799";
const EMAIL_ADDRESS = "info@pragyalims.com";

const PREDEFINED_QUESTIONS = [
  "What is Pragya LIMS?",
  "What are the key features?",
  "Is the software customizable?",
  "What is the installation process?",
  "Can it integrate with our instruments?",
  "Is it compliant with regulations?",
  "How to schedule a demo?",
  "What kind of support is offered?",
  "What about pricing?",
];

// --- SVG Icon Components ---
const ChatbotIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
    <line x1="9" y1="10" x2="15" y2="10"></line>
    <line x1="9" y1="14" x2="12" y2="14"></line>
  </svg>
);

const ArrowSendIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const TypingIndicator = () => (
  <div className="flex justify-start">
    <div className="p-2.5 rounded-lg bg-white text-gray-800 shadow-sm flex items-center space-x-1.5">
      <span className="text-xs font-medium text-gray-600">Typing</span>
      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
    </div>
  </div>
);

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [view, setView] = useState("questions");
  const chatRef = useRef(null);

  // WhatsApp form state
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    lab: "",
    city: "",
    remarks: "",
  });

  // Animation states
  const [showWhatsAppIcon, setShowWhatsAppIcon] = useState(false);
  const [showChatbotIcon, setShowChatbotIcon] = useState(false);
  const [whatsAppPosition, setWhatsAppPosition] = useState("bottom-4");

  // Contact links
  const ContactLink = ({ text }) => (
    <a
      href={`https://wa.me/${WHATSAPP_NUMBER}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-teal-600 font-semibold hover:underline"
    >
      {text || `WhatsApp at ${DISPLAY_PHONE_NUMBER}`}
    </a>
  );
  const EmailLink = () => (
    <a
      href={`mailto:${EMAIL_ADDRESS}`}
      className="text-teal-600 font-semibold hover:underline"
    >
      {EMAIL_ADDRESS}
    </a>
  );

  // --- useEffect for animations and auto-open ---
  useEffect(() => {
    const timer1 = setTimeout(() => {
      setShowWhatsAppIcon(true);
    }, 0);

    const timer2 = setTimeout(() => {
      setShowChatbotIcon(true);
      setWhatsAppPosition("bottom-20");
    }, 5000);

    const hasAutoOpened = sessionStorage.getItem("chatbotAutoOpened");
    let timer3 = null;

    if (!hasAutoOpened) {
      timer3 = setTimeout(() => {
        const now = new Date();
        const timeString = now.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

        setMessages([
          {
            text: `Welcome to Pragya LIMS Support! It's ${timeString}. How can I assist you today? Feel free to select a question below.`,
            isBot: true,
          },
        ]);
        setView("chat");
        setIsOpen(true);
        sessionStorage.setItem("chatbotAutoOpened", "true");
      }, 13000);
    }

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      if (timer3) clearTimeout(timer3);
    };
  }, []);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // Chat helpers
  const addBotMessage = (text) => {
    setMessages((prev) => [...prev, { text, isBot: true }]);
  };
  const addUserMessage = (text) => {
    setMessages((prev) => [...prev, { text, isBot: false }]);
  };

  const handleQuestionSelect = (question) => {
    addUserMessage(question);
    setView("chat");
    setIsTyping(true);
    setTimeout(() => {
      respondToMessage(question.toLowerCase());
      setIsTyping(false);
    }, 1500);
  };

  const respondToMessage = (message) => {
    let response = (
      <>
        I'm sorry, I don't have information on that. For specific queries,
        please contact our support team on <ContactLink />.
      </>
    );

    if (message.includes("what is pragya lims")) {
      response =
        "Pragya LIMS is an AI-powered Laboratory Information Management System designed to streamline lab operations with precision sample tracking, seamless integrations, and robust compliance tools.";
    } else if (message.includes("schedule a demo")) {
      response = (
        <>
          To schedule a personalized demo, please click the 'Schedule a Demo'
          button on our website or contact our sales team via <ContactLink />.
        </>
      );
    } else if (message.includes("key features")) {
      response =
        "Key features include: Sample Management, Workflow Automation, Quality Control, Instrument Integration, Audit Trails, and comprehensive Reporting. Visit our website for a full list!";
    } else if (message.includes("support is offered")) {
      response = (
        <>
          We offer dedicated support via <ContactLink /> and email at{" "}
          <EmailLink /> during business hours (9 AM - 6 PM IST, Mon-Fri).
        </>
      );
    } else if (message.includes("pricing")) {
      response = (
        <>
          Our pricing is tailored to your lab's specific needs. For a detailed
          quote, please <ContactLink text="get in touch with our sales team" />{" "}
          to discuss your requirements.
        </>
      );
    } else if (message.includes("customizable")) {
      response =
        "Yes, Pragya LIMS is highly customizable. We work with you to configure workflows, reports, and modules to perfectly match your laboratory's unique processes.";
    } else if (message.includes("installation process")) {
      response =
        "Pragya LIMS is an on-premise solution installed directly on your local servers for maximum security and control. Our expert team handles the complete installation and setup process for you.";
    } else if (message.includes("integrate with our instruments")) {
      response =
        "Absolutely. Pragya LIMS is designed for seamless integration with a wide range of laboratory instruments to automate data capture and reduce manual errors.";
    } else if (message.includes("compliant with regulations")) {
      response =
        "Yes, our system is built to help you comply with major industry regulations like ISO 17025, GxP, and 21 CFR Part 11, featuring audit trails and secure data handling.";
    }
    addBotMessage(response);
  };

  // --- WhatsApp Form handlers ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitForm = () => {
    const { name, lab, city, remarks } = formData;

    if (!name || !lab || !city) {
      alert("Please fill in all required fields (Name, Lab, City).");
      return;
    }

    let message =
      `Hello, my name is ${name}.\n` +
      `I am from ${lab}, located in ${city}.\n` +
      `${remarks ? `Remarks: ${remarks}\n` : ""}` +
      `I’d like to know more about Pragya LIMS.`;

    // Replace all newlines with WhatsApp's line break code
    message = message.replace(/\n/g, "%0A");

    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;

    window.open(whatsappUrl, "_blank");
    setShowForm(false);
    setFormData({ name: "", lab: "", city: "", remarks: "" });
  };

  // --- Views ---
  const ChatView = () => (
    <>
      <div
        ref={chatRef}
        className="flex-grow overflow-y-auto p-3 space-y-4 bg-gray-50"
      >
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`flex ${msg.isBot ? "justify-start" : "justify-end"}`}
          >
            <div
              className={`p-2.5 rounded-lg max-w-[85%] text-xs shadow-sm ${
                msg.isBot ? "bg-white text-gray-800" : "bg-[#18A594] text-white"
              }`}
            >
              {msg.text}
            </div>
          </div>
        ))}
        {isTyping && <TypingIndicator />}
      </div>
      <div className="p-2 border-t border-gray-200 bg-white flex-shrink-0">
        <button
          onClick={() => setView("questions")}
          className="w-full text-center text-xs font-semibold text-[#18A594] py-2 px-3 rounded-md hover:bg-teal-50 transition-colors"
        >
          ‹ Select a Question
        </button>
      </div>
    </>
  );

  const QuestionView = () => (
    <div className="p-3 bg-gray-50 flex-grow overflow-y-auto">
      <p className="text-xs text-gray-600 mb-3 font-medium text-center">
        Hello! Please select a topic to get started.
      </p>
      <div className="space-y-2">
        {PREDEFINED_QUESTIONS.map((q, i) => (
          <div
            key={i}
            className="flex items-center justify-between bg-white p-2.5 rounded-md cursor-pointer hover:bg-gray-100 transition-colors duration-200 shadow-sm"
            onClick={() => handleQuestionSelect(q)}
          >
            <span className="text-xs text-gray-700">{q}</span>
            <ArrowSendIcon className="w-3.5 h-3.5 text-[#18A594] flex-shrink-0 ml-2" />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {/* WhatsApp Icon */}
      {showWhatsAppIcon && (
        <div
          className={`fixed right-4 w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center cursor-pointer hover:scale-110 shadow-lg z-50 transition-all duration-300 ease-in-out ${whatsAppPosition}`}
          onClick={() => setShowForm(true)}
        >
          <WhatsAppIcon className="w-6 h-6 text-white" />
        </div>
      )}

      {/* Chatbot Icon */}
      {showChatbotIcon && (
        <div
          className="fixed bottom-4 right-4 w-12 h-12 bg-[#18A594] rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-transform duration-300 shadow-lg z-50"
          onClick={() => setIsOpen(!isOpen)}
        >
          <ChatbotIcon className="w-6 h-6 text-white" />
        </div>
      )}

      {/* Chatbot Window */}
      {isOpen && (
        <div className="fixed bottom-20 right-4 w-72 max-h-[500px] bg-white rounded-xl shadow-2xl flex flex-col z-50 overflow-hidden border border-gray-200">
          <div className="bg-[#18A594] text-white p-3 flex justify-between items-center rounded-t-xl flex-shrink-0">
            <span className="text-sm font-semibold">Pragya LIMS Support</span>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white text-2xl font-bold focus:outline-none hover:text-gray-200 transition-colors"
            >
              &times;
            </button>
          </div>
          {view === "questions" ? <QuestionView /> : <ChatView />}
        </div>
      )}

      {/* --- MODIFIED SECTION START --- */}
      {/* WhatsApp Form Modal */}
      {showForm && (
        <div className="modelBg fixed inset-0 flex items-center justify-center bg-opacity-40 z-50 p-4">
          <div className="bg-white p-5 rounded-xl w-full max-w-sm shadow-lg border-t-4 border-[#25D366]">
            <div className="flex items-center justify-center mb-4">
              <h2 className="text-base font-semibold text-gray-800">
                Send your details on
              </h2>
              <WhatsAppIcon className="w-5 h-5 ml-2 mb-1 text-[#25D366]" />
            </div>

            <form className="space-y-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  Your Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="e.g., John Doe"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 p-2 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-[#30BBAA]"
                />
              </div>

              <div>
                <label
                  htmlFor="lab"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  Lab Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="lab"
                  name="lab"
                  placeholder="e.g., Acme Labs"
                  value={formData.lab}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 p-2 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-[#30BBAA]"
                />
              </div>

              <div>
                <label
                  htmlFor="city"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  placeholder="e.g., New York"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 p-2 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-[#30BBAA]"
                />
              </div>

              <div>
                <label
                  htmlFor="remarks"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  Remarks
                </label>
                <textarea
                  id="remarks"
                  name="remarks"
                  placeholder="Any specific questions... (optional)"
                  value={formData.remarks}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full border border-gray-300 p-2 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-[#30BBAA]"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmitForm}
                  className="px-4 py-2 text-sm font-medium rounded-md bg-[#25D366] text-white hover:bg-[#20b756] focus:outline-none flex items-center"
                >
                  <WhatsAppIcon className="w-4 h-4 mr-2" />
                  Send
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* --- MODIFIED SECTION END --- */}
    </>
  );
};

export default Chatbot;
